import { getDeviceId, getDeviceName } from "@/lib/device";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, tokenStore } from "@/lib/api";
import { isSupabaseMode } from "@/lib/backend";
const SupabaseAuthProvider = React.lazy(() => import("@/supabase/AuthProvider")
  .then((m) => ({ default: m.SupabaseAuthProvider })));

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  return isSupabaseMode
    ? <React.Suspense fallback={<p role="status">Loading Phoenix…</p>}>
        <SupabaseAuthProvider context={AuthContext}>{children}</SupabaseAuthProvider>
      </React.Suspense>
    : <LegacyAuthProvider>{children}</LegacyAuthProvider>;
}

function LegacyAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [initializing, setInitializing] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      tokenStore.setUser(data);
      setUser(data);
      return data;
    } catch (e) {
      // No response = offline: keep working from the last known profile.
      // The server still enforces permissions once requests resume.
      const cached = !e?.response ? tokenStore.getUser() : null;
      if (cached) {
        setUser(cached);
        return cached;
      }
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    (async () => {
      if (tokenStore.getAccess()) {
        await fetchMe();
      }
      setInitializing(false);
    })();
  }, [fetchMe]);

  const login = async (identifier, password) => {
    const { data } = await api.post(
      "/auth/login",
      { identifier, password },
      { headers: { "X-Device-Id": getDeviceId(), "X-Device-Name": getDeviceName() } }
    );
    tokenStore.set(data.tokens.access_token, data.tokens.refresh_token);
    tokenStore.setUser(data.user);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    tokenStore.set(data.tokens.access_token, data.tokens.refresh_token);
    tokenStore.setUser(data.user);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    const refresh_token = tokenStore.getRefresh();
    try {
      if (refresh_token) {
        await api.post("/auth/logout", { refresh_token });
      }
    } catch (_) {
      /* swallow */
    } finally {
      tokenStore.clear();
      setUser(null);
    }
  };

  // Sprint 10 — convenient accessors for permission-aware UI.
  const roles = useMemo(() => user?.roles || [], [user]);
  const permissions = useMemo(() => user?.permissions || [], [user]);
  const hasPermission = useCallback(
    (perm) => {
      if (!permissions.length) return false;
      if (Array.isArray(perm)) return perm.some((p) => permissions.includes(p));
      return permissions.includes(perm);
    },
    [permissions]
  );
  const hasRole = useCallback(
    (role) => {
      if (!user) return false;
      if (roles.includes(role)) return true;
      return user.role === role; // legacy single-role fallback
    },
    [user, roles]
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        initializing,
        login,
        register,
        logout,
        refreshMe: fetchMe,
        roles,
        permissions,
        hasPermission,
        hasRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
